import { headers, initAction } from "@commercekit/remix/lib.server";
import type { ActionFunctionArgs, LinksFunction } from "@remix-run/node";
import { ShouldRevalidateFunctionArgs } from "@remix-run/react";
import { withSentry } from "@sentry/remix";
import { ensureTrailingSlash } from "@commercekit/shared";
import { metaHelper } from "~/lib/meta";
import customStylesheetUrl from "~/styles/custom.css?url";
import spritesStylesheetUrl from "~/styles/freakflags.css?url";
import GeneralErrorBoundary from "./components/atoms/GeneralErrorBoundary";
import { Document, Root } from "./components/layout/Layout";
import { loader } from "./components/layout/Layout.server";

export { headers, loader };

export const action = async (args: ActionFunctionArgs) => {
  const helper = await initAction(args);
  return helper.response();
};

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: customStylesheetUrl },
  { rel: "stylesheet", href: spritesStylesheetUrl },
  {
    rel: "apple-touch-icon",
    href: "/apple-touch-icon.png",
  },
  {
    rel: "manifest",
    href: "/site.webmanifest",
    crossOrigin: "anonymous",
    type: "application/manifest+json",
  },
  {
    rel: "icon",
    href: "/favicon-32x32.png",
    type: "image/png",
    sizes: "32x32",
  },
  {
    rel: "icon",
    href: "/favicon-16x16.png",
    type: "image/png",
    sizes: "16x16",
  },
  {
    rel: "shortcut icon",
    href: "/favicon.ico",
    type: "image/x-icon",
    crossOrigin: "anonymous",
  },
  { rel: "preconnect", href: "https://prismic-io.s3.amazonaws.com" },
  { rel: "preconnect", href: "https://wovar-rb2-dev.cdn.prismic.io" },
  { rel: "preconnect", href: "https://www.googletagmanager.com" },
  { rel: "dns-prefetch", href: "https://sst.wovar.nl" },
  { rel: "dns-prefetch", href: "https://static.klaviyo.com" },
  { rel: "dns-prefetch", href: "https://cdn.cookie-script.com" },
  { rel: "dns-prefetch", href: "https://cdn-widgetsrepository.yotpo.com" },
  { rel: "dns-prefetch", href: "https://static.hotjar.com" },
  { rel: "dns-prefetch", href: "https://static.widget.trengo.eu" },
  { rel: "preconnect", href: "https://cdn-4.convertexperiments.com" },
  {
    rel: "preload",
    href: "/assets/fonts/icons.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Lato/Lato-Light.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Lato/Lato-Regular.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Lato/Lato-Bold.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Lato/Lato-Black.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Poppins/Poppins-Light.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/assets/fonts/Poppins/Poppins-Regular.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
];

export const meta = ({ matches }) => {
  return [...metaHelper(matches), { name: "theme-color", content: "#ffffff" }];
};

// Mirrors the exemptions in the root loader's own trailing-slash redirect.
const TRAILING_SLASH_EXEMPT = [/^\/healthz$/, /^\/resources(?:\/|$)/];

export const shouldRevalidate = ({
  formAction,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
  // Don't revalidate when /cart action is called
  if (formAction === "/cart") {
    return false;
  }
  // Root data is global: only an action, an explicit revalidate() or root's own 301 can change it.
  const isExplicitRevalidation = currentUrl.href === nextUrl.href;
  if (
    !formAction &&
    !isExplicitRevalidation &&
    ensureTrailingSlash(nextUrl.pathname, TRAILING_SLASH_EXEMPT) ===
      nextUrl.pathname
  ) {
    return false;
  }
  return defaultShouldRevalidate;
};

export function ErrorBoundary() {
  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document locale="nl-nl">
      <GeneralErrorBoundary />
    </Document>
  );
}

export default withSentry(Root);
